import React, { useCallback, useContext, useRef } from 'react';
import { DeviceSyncContext } from '../../context/DeviceSyncContext';
import { useLazyQuery } from '@apollo/client';
import { SYNC_DEVICE_DATA } from '../../services/user.service';
import _ from 'lodash';
import { DeviceTypeEnum } from '../../types/device-type.enum';
import { DeviceType } from '../../types/device.type';
import { TrackingContext } from '../../context/TrackingContext';
import { getUnixTime } from 'date-fns';
import { TrackingEvent } from '../../types/tracking-events.enum';
import useAuth from '../../auth/use-auth';
import { deleteDeviceSyncFlag } from '../../util/local-storage';
import { UserType } from '../../types/user.type';

interface Props {
    children: React.ReactNode;
}

const DeviceSyncProvider = ({ children }: Props) => {
    const { sendEvent } = useContext(TrackingContext);
    const { user, setUser } = useAuth();

    const deviceSyncDuration = useRef<number | undefined>(undefined);

    const [
        syncDevicesQuery,
        { loading: syncDeviceDataLoading },
    ] = useLazyQuery<{
        syncDeviceData: Partial<DeviceType>[];
    }>(SYNC_DEVICE_DATA, {
        onCompleted: (data) => {
            if (data && data.syncDeviceData) {
                updateUserDevices(data.syncDeviceData);
                deleteDeviceSyncFlag();
                if (deviceSyncDuration.current) {
                    const syncDuration =
                        getUnixTime(new Date()) - deviceSyncDuration.current;
                    sendEvent(TrackingEvent.DEVICE_SYNC_FINISHED, {
                        duration: syncDuration,
                    });
                    deviceSyncDuration.current = undefined;
                }
            }
        },
        onError: () => {
            updateUserDevices([]);
            deleteDeviceSyncFlag();
        },
    });

    const syncDevices = useCallback(
        (devices: (DeviceTypeEnum | undefined)[] = [], user?: UserType) => {
            deviceSyncDuration.current = getUnixTime(new Date());
            syncDevicesQuery({
                variables: {
                    devices: devices.filter((device) => device !== undefined),
                    memberId: user && user.memberId,
                },
            });
        },
        [syncDevicesQuery],
    );

    const getDeviceSyncDate = (
        deviceType: DeviceTypeEnum,
        user?: UserType,
    ): string | undefined => {
        if (user?.devices) {
            const devices = user.devices.filter(
                (device) => device.deviceType === deviceType,
            );
            if (devices.length > 0) {
                return devices[0].lastRefreshDate;
            }
        }
        return undefined;
    };

    const updateUserDevices = (devices: Partial<DeviceType>[] = []): void => {
        if (user) {
            const newUserContext = _.clone(user);
            newUserContext.devices = _.unionBy(
                devices,
                user?.devices || [],
                'deviceType',
            );
            setUser(newUserContext);
        }
    };

    const contextValue = {
        syncDeviceDataLoading,
        syncDevices,
        getDeviceSyncDate,
        updateUserDevices,
    };

    return (
        <DeviceSyncContext.Provider value={contextValue}>
            {children}
        </DeviceSyncContext.Provider>
    );
};

export default DeviceSyncProvider;
